import { Alert, Button, Group, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, podatki, type Shema } from '../api/odjemalec';
import { NapakaApi, sporocilo } from '../api/napaka';
import { Kartica, StanjeNalaganja } from '../postavitev/Podatki';
import { useSeja } from '../seja/useSeja';

type Nastavitve = Shema['OpisVozilNastavitve'];

/**
 * Nastavitve → Opis vozil (novo v spletu): fiksno besedilo pred opisom in za njim, ki se doda vsem vozilom (javna stran
 * in oglas za okno; sprememba velja takoj tudi za obstoječa vozila, pri vozilu se ne ureja), in navodila za umetno
 * inteligenco, ki piše opise.
 */
export function OpisVozil() {
  const poizvedba = useQuery({
    queryKey: ['opis-vozil'],
    queryFn: ({ signal }) => podatki(api.GET('/api/opis-vozil', { signal })),
  });
  if (!poizvedba.data) {
    return (
      <StanjeNalaganja
        napaka={poizvedba.error}
        ponovi={() => void poizvedba.refetch()}
        naslov="Nastavitev opisa vozil ni bilo mogoče naložiti"
      />
    );
  }
  return <Obrazec key={poizvedba.dataUpdatedAt} shranjeno={poizvedba.data} />;
}

function Obrazec({ shranjeno }: { shranjeno: Nastavitve }) {
  const odjemalec = useQueryClient();
  const seja = useSeja();
  const ai = seja.status === 'prijavljen' && !!seja.instanca.umetnaInteligenca;
  const [n, setN] = useState({
    uvod: shranjeno.uvod ?? '',
    zakljucek: shranjeno.zakljucek ?? '',
    navodila: shranjeno.navodila ?? '',
  });
  const [polja, setPolja] = useState<Record<string, string>>({});
  const [napaka, setNapaka] = useState<string | null>(null);
  const [dela, setDela] = useState(false);
  const spremenjeno =
    n.uvod !== (shranjeno.uvod ?? '') ||
    n.zakljucek !== (shranjeno.zakljucek ?? '') ||
    n.navodila !== (shranjeno.navodila ?? '');

  const shrani = async () => {
    setNapaka(null);
    setPolja({});
    setDela(true);
    try {
      odjemalec.setQueryData(['opis-vozil'], await podatki(api.PUT('/api/opis-vozil', { body: n })));
      notifications.show({ color: 'green', message: 'Nastavitve opisa vozil so shranjene.' });
    } catch (e) {
      if (e instanceof NapakaApi && Object.keys(e.polja).length > 0) {
        setPolja(e.polja);
        setNapaka('Ni shranjeno: popravite označena polja.');
      } else {
        setNapaka(`Ni shranjeno: ${sporocilo(e)}`);
      }
    } finally {
      setDela(false);
    }
  };

  return (
    <Stack gap="md">
      <Kartica naslov="Fiksno besedilo">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Doda se pred opisom in za njim pri vseh vozilih – na javni strani vozila in v oglasu za okno – tudi pri
            vozilih brez opisa. Sprememba velja takoj za vsa vozila; pri vozilu se to besedilo ne ureja.
          </Text>
          <Textarea
            size="md"
            label="Pred opisom"
            placeholder="npr. Vozilo prodaja Avtohiša d.o.o. – vsa vozila s preverjeno zgodovino."
            value={n.uvod}
            onChange={(e) => setN({ ...n, uvod: e.currentTarget.value })}
            error={polja.uvod}
            autosize
            minRows={2}
            maxRows={8}
          />
          <Textarea
            size="md"
            label="Za opisom"
            placeholder="npr. Možnost financiranja, menjave in podaljšane garancije. Pokličite za ogled."
            value={n.zakljucek}
            onChange={(e) => setN({ ...n, zakljucek: e.currentTarget.value })}
            error={polja.zakljucek}
            autosize
            minRows={2}
            maxRows={8}
          />
          <Text size="xs" c="dimmed">
            V oglasu za okno je prostora za približno 600 znakov; fiksno besedilo ima prednost, opis vozila se po
            potrebi skrajša.
          </Text>
        </Stack>
      </Kartica>
      <Kartica naslov="Navodila za umetno inteligenco">
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Po teh navodilih umetna inteligenca piše opise vozil (gumb »Ustvari opis« pri urejanju vozila).
            {!ai && ' Umetna inteligenca je zdaj izklopljena (Nastavitve → Umetna inteligenca).'}
          </Text>
          <Textarea
            size="md"
            label="Navodila"
            placeholder="npr. Piši v prvi osebi množine in prijazno. Opremo naštej v alinejah. Vedno omeni, da je vozilo pregledano na našem servisu."
            value={n.navodila}
            onChange={(e) => setN({ ...n, navodila: e.currentTarget.value })}
            error={polja.navodila}
            autosize
            minRows={3}
            maxRows={12}
          />
        </Stack>
      </Kartica>
      {napaka && (
        <Alert color="red" variant="light" role="alert">
          {napaka}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button onClick={() => void shrani()} loading={dela} disabled={!spremenjeno}>
          Shrani
        </Button>
      </Group>
    </Stack>
  );
}
